<template>
  <transition name="slide">
    <div
      v-if="show"
      :class="{
        'lazy-zendesk--mobile': isMobile,
      }"
      class="lazy-zendesk"
    >
      <button
        class="lazy-zendesk__button"
        type="button"
        @click="activateZendesk"
      >
        <span class="lazy-zendesk__icon">
          <svg
            id="Layer_1"
            x="0"
            y="0"
            viewBox="0 0 15 16"
            xml:space="preserve"
            aria-hidden="true"
          >
            <path
              d="M1.3,16c-0.7,0-1.1-0.3-1.2-0.8c-0.3-0.8,0.5-1.3,0.8-1.5c0.6-0.4,0.9-0.7,1-1c0-0.2-0.1-0.4-0.3-0.7c0,0,0-0.1-0.1-0.1 C0.5,10.6,0,9,0,7.4C0,3.3,3.4,0,7.5,0C11.6,0,15,3.3,15,7.4s-3.4,7.4-7.5,7.4c-0.5,0-1-0.1-1.5-0.2C3.4,15.9,1.5,16,1.5,16 C1.4,16,1.4,16,1.3,16z M3.3,10.9c0.5,0.7,0.7,1.5,0.6,2.2c0,0.1-0.1,0.3-0.1,0.4c0.5-0.2,1-0.4,1.6-0.7c0.2-0.1,0.4-0.2,0.6-0.1 c0,0,0.1,0,0.1,0c0.4,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2.4,5.5-5.4S10.5,2,7.5,2C4.5,2,2,4.4,2,7.4c0,1.2,0.4,2.4,1.2,3.3 C3.2,10.8,3.3,10.8,3.3,10.9z"
            />
          </svg>
        </span>
        <span class="lazy-zendesk__text"> {{ buttonText }} </span>
      </button>
    </div>
  </transition>
</template>

<script>
const HAS_ACTIVATED_ZENDESK_COOKIE = 'hasActivatedZendesk'
import Cookies from 'js-cookie'
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'

import enGB from './locales/en-GB.json'
import enUS from './locales/en-US.json'
import sv from './locales/sv.json'

export default defineComponent({
  name: 'LazyZendesk',
  i18n: {
    messages: {
      sv,
      'en-US': enUS,
      'en-GB': enGB,
    },
  },
  setup() {
    const { t } = useI18n()

    return {
      buttonText: t('MLazyZendesk.button_text'),
    }
  },
  data() {
    return {
      show: false,
      isMobile: false,
    }
  },
  mounted() {
    // Check if it's mobile on mount
    this.isMobile = this.isMobileCheck()
    // If we have never clicked the button
    // just show the lazyload button
    if (!Cookies || !Cookies.get(HAS_ACTIVATED_ZENDESK_COOKIE)) {
      this.show = true
    } else {
      // If we have clicked the button before
      // then we need to load zendesk in case they've been navigating
      this.$zendesk.load()
    }
  },
  methods: {
    activateZendesk() {
      // Load the zendesk script then open it up
      // Note: this.$zendesk is set from the vue-zendesk & nuxt-zendesk plugins
      // that we use in the consuming apps.
      this.$zendesk.load()
      Cookies.set(HAS_ACTIVATED_ZENDESK_COOKIE, '1')
      this.$zendesk.$on('loaded', () => {
        this.$zendesk.open()
        this.show = false
      })
    },
    isMobileCheck() {
      // Copied from a mobile detection from the Zendesk team
      // to match the zendesk widget
      const ua = navigator.userAgent.toLowerCase()
      const platform = navigator.platform.toLowerCase()
      const platformName = ua.match(/ip(?:ad|od|hone)/)
        ? 'ios'
        : (ua.match(/(?:webos|android)/) ||
            platform.match(/mac|win|linux/) || ['other'])[0]
      const isMobile = /ios|android|webos/.test(platformName)
      return isMobile
    },
  },
})
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/screen-reader';

/* Color values need to be static to match the color applied in the consuming app's zendesk config.  */
$brand-bg-manypets: #092110;

/**
* These styles are copied directly from Zendesk's native styling. They need to remain static,
* to assure we always match the widget's post-click style, which is shown by Zendesk natively.
*/
.lazy-zendesk {
  bottom: 14px;
  font-size: 14px;
  position: fixed;
  right: 20px;
  z-index: 99999;
  &__button {
    background-color: $brand-bg-manypets;
    border: 0 none;
    border-radius: 999rem;
    bottom: 0;
    color: #fff;
    cursor: pointer;
    fill: #fff;
    font-family:
      system-ui,
      -apple-system,
      'Segoe UI',
      Roboto,
      Oxygen-Sans,
      Ubuntu,
      Cantarell,
      'Helvetica Neue',
      Arial,
      sans-serif;
    font-size: 1em;
    font-weight: 700;
    letter-spacing: 0.6px;
    line-height: 1.15;
    padding: 13px 22px;
  }
  &__icon {
    display: inline-block;
    font-size: inherit;
    line-height: 0.8;
    padding-right: 0.57143em;
    vertical-align: middle;
    svg {
      font-size: inherit;
      height: 1.42857em;
      min-height: 1.42857em;
      min-width: 1.42857em;
      overflow: hidden;
      width: 1.42857em;
    }
  }
  &__text {
    display: inline-block;
    font-size: 15px;
    vertical-align: middle;
  }
  &--mobile {
    .lazy-zendesk__button {
      padding: 1em;
    }
    .lazy-zendesk__icon {
      padding-right: 0;
    }
    .lazy-zendesk__text {
      @include screen-reader.sr-only;
    }
  }
  &.slide-enter-active,
  &.slide-leave-active {
    transition:
      transform 0.2s var(--animation-timing),
      opacity 0.2s var(--animation-timing);
  }
  &.slide-enter-from,
  &.slide-leave-to {
    opacity: 0;
    transform: translateY(1em);
  }
}
</style>
